import { useLocalization } from '@lib/localization';
import type { Inspection } from '@lib/spproject/types';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Card, CardContent, IconButton, InputBase, Pagination, Typography } from '@mui/material';
import { useGetChecklistsQuery } from '@store/api/checklistEndpoints';
import type { IInspection } from '@store/api/inspectionEndpoints';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { StyledPaper } from './Checklist.styled';
import ChecklistAccordion from './ChecklistAccordion';
import PoiDetailModal, { type DetailedPOI } from './Detail';

interface ChecklistsTabProps {
  projectId: string;
  inspections: IInspection[];
}

const ITEMS_PER_PAGE = 10;

const ChecklistsTab: FC<ChecklistsTabProps> = ({ projectId, inspections }) => {
  const { t } = useLocalization();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedPOI, setSelectedPOI] = useState<Inspection.PointOfInterest>();
  const [selectedTemplate, setSelectedTemplate] = useState<IInspection>();
  const [relatedInspections, setRelatedInspections] = useState<DetailedPOI[]>([]);
  const [page, setPage] = useState(1);

  const query = searchTerm.trim().toLowerCase();

  const { data: checklists, isLoading } = useGetChecklistsQuery({
    projectId,
    include: ['notes', 'pointOfInterests']
  });

  const inspectionsByTemplate = useMemo(() => {
    const map: Record<string, IInspection[]> = {};

    for (const inspection of inspections) {
      const templateId = inspection.info.baseTemplateId;
      (map[templateId] ??= []).push(inspection);
    }

    return map;
  }, [inspections]);


  const groupedChecklists = useMemo(() => (
    checklists?.reduce<Record<string, IInspection[]>>((groups, inspection) => {
      groups[inspection.info.id] = inspectionsByTemplate[inspection.info.id] ?? [];
      return groups;
    }, {}) ?? {}
  ), [checklists, inspectionsByTemplate]);

  const filteredChecklists = useMemo(() => {
    if (!checklists) return [];

    if (!query) return checklists;

    return checklists.filter(({ info, pointOfInterests }) => {
      const name = info.name.toLowerCase();
      if (name.includes(query)) return true;

      return pointOfInterests?.some(p =>
        p.name.toLowerCase().includes(query) ||
        (p.description ?? '').toLowerCase().includes(query)
      );
    });
  }, [checklists, query]);

  const pagedChecklists = useMemo(() => {
    const totalPages = Math.ceil(filteredChecklists.length / ITEMS_PER_PAGE);
    const data = filteredChecklists.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

    return { totalPages, data };
  }, [filteredChecklists, page]);

  const toggleExpand = useCallback((inspectionId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(inspectionId)) next.delete(inspectionId);
      else next.add(inspectionId);
      return next;
    });
  }, []);

  const handlePoiClick = useCallback((poi: Inspection.PointOfInterest, template: IInspection) => {
    const relatedInspection = inspections.reduce<DetailedPOI[]>((result, i) => {
      const entry = i.pointOfInterests?.find(p => p.id === poi.id);
      if (entry) result.push({ entry, info: i.info });
      return result;
    }, []);

    setRelatedInspections(relatedInspection);
    setSelectedPOI(poi);
    setSelectedTemplate(template);
    setOpen(true);
  }, [inspections]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  return (
    <Box sx={{ width: '100%' }}>
      <StyledPaper>
        <IconButton sx={{ p: '10px' }} aria-label="search">
          <SearchIcon />
        </IconButton>
        <InputBase
          sx={{ ml: 1, flex: 1 }}
          placeholder={t('Project_Checklist_Search')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </StyledPaper>

      {!isLoading && pagedChecklists.data.map(inspection => (
        <ChecklistAccordion
          key={inspection.info.id}
          checklist={inspection}
          inspections={groupedChecklists[inspection.info.id] ?? []}
          isExpanded={expanded.has(inspection.info.id)}
          onToggleExpand={toggleExpand}
          onPoiClick={handlePoiClick}
        />
      ))}

      {!isLoading && pagedChecklists.data.length === 0 && (
        <Card sx={{ border: 'none' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography color="text.secondary">
              {searchTerm ? t('Checklists_NotMatch') : t('Checklists_NotFound')}
            </Typography>
          </CardContent>
        </Card>
      )}

      {pagedChecklists.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={pagedChecklists.totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}

      {(selectedTemplate && selectedPOI) && (
        <PoiDetailModal
          open={open}
          onClose={() => setOpen(false)}
          pois={relatedInspections}
          template={selectedTemplate.info}
          current={selectedPOI}
        />
      )}

    </Box>
  );
};

export default ChecklistsTab;
